// A small copy of the case-study fields that the ALWAYS-loaded parts of the site
// need: RouteMeta (page titles for /work/<slug>), FeaturedWork (the four Home
// cards) and the route chunk loader (which editorial layout a slug renders).
//
// Why a copy: src/data/caseStudies.js is ~140 KB of narrative. Importing it from
// those three places put all of it in the main bundle, parsed before the homepage
// could render, even though only the Work and case-study pages read it - and they
// are now separate chunks that import the full file themselves.
//
// It must agree with caseStudies.js, and caseStudyIndex.test.js fails the moment
// it does not: change a title, description, tag, colour, badge, featured flag,
// layout, alias or demo there, and the test names the field to copy here.

const caseStudyIndex = [
  {
    id: "budgetfamily",
    slug: "budgetfamily",
    aliasSlugs: [
      "hari-kudumbam"
    ],
    title: "Family Finance Built for a Three-Generation Household",
    subtitle: "FamilyWorth: Family Wealth, Honestly Counted",
    description: "A family finance app built for my own three-generation household, scoping gold, bank accounts, monthly expenses, property, savings and future plans. Thirteen modules in daily use, from a share sheet that sends one account number instead of a passbook photograph to a milk ledger that replaced the vendor's paper notebook.",
    color: "#6FEAC8",
    highlightBadge: "Daily-Use App",
    tags: [
      "Consumer App",
      "UX Research",
      "Accessibility",
      "Next.js",
      "Firebase"
    ],
    hasDemo: true
  },
  {
    id: "familyworth-gold-vault",
    slug: "familyworth-gold-vault",
    layout: "editorial-gold",
    title: "A Piece of Gold Needed a State, Not Just a Value",
    subtitle: "FamilyWorth: Gold Vault",
    description: "A deep dive on one module of FamilyWorth. Counting a household's gold piece by piece showed that monetary value alone could not describe its position: some of it was pledged at a bank, and some had arrived as moi, expected back at equal or greater weight years later. So a piece of gold was given a state, and the headline total is shown net of what is owed.",
    color: "#6FEAC8",
    highlightBadge: "Product Model Deep Dive",
    tags: [
      "Product Modelling",
      "UX Research",
      "Consumer App",
      "Cultural Context"
    ],
    hasDemo: true
  },
  {
    id: "supplier-scorecard",
    slug: "supplier-scorecard",
    title: "Transforming Supplier Evaluation from Spreadsheets to Strategic Dashboards",
    subtitle: "Supplier Scorecard Platform",
    description: "Redesigned the supplier assessment platform used across 22 business segments and 13 categories, replacing a legacy ASP.NET interface with a modern React-based scoring system.",
    color: "#7FD1FF",
    highlightBadge: "Enterprise Scale",
    tags: [
      "Enterprise UX",
      "Data Visualization",
      "React",
      "AG Grid"
    ],
    hasDemo: true
  },
  {
    id: "change-management-console",
    slug: "change-management-console",
    title: "Redesigning Manufacturing Change Management for Semiconductor Teams",
    subtitle: "Change Management Console",
    description: "Led the complete UX redesign of the change-management platform, transforming a complex multi-step approval workflow into visual status pipelines with role-based dashboards.",
    featured: true,
    color: "#FFAACB",
    highlightBadge: "Team of the Quarter",
    tags: [
      "Enterprise UX",
      "Workflow Design",
      "React",
      "Design Systems"
    ],
    hasDemo: false
  },
  {
    id: "procurement-analytics",
    slug: "procurement-analytics",
    title: "Bringing Clarity to Enterprise Procurement with Data-Rich Dashboards",
    subtitle: "Procurement Analytics Dashboard",
    description: "Designed and built the procurement dashboard purchasing teams use to manage supplier contracts, spend analysis, and part tracking across 10,000+ components.",
    color: "#FFB59E",
    highlightBadge: "Process Innovation",
    tags: [
      "Enterprise UX",
      "Data Dashboards",
      "Charts",
      "AG Grid"
    ],
    hasDemo: true
  },
  {
    id: "engineering-data-hub",
    slug: "engineering-data-hub",
    title: "Streamlining Engineering Data Exchange Across Global Teams",
    subtitle: "Engineering Data Hub",
    description: "Redesigned the engineering data exchange platform that enables global teams to share specifications, test results, and manufacturing data across Vertex Microsystems' worldwide operations.",
    color: "#6FEAC8",
    highlightBadge: "Data Architecture",
    tags: [
      "Enterprise UX",
      "Search & Filter",
      "Data Exchange",
      "React"
    ],
    hasDemo: true
  },
  {
    id: "supplier-data-console",
    slug: "supplier-data-console",
    title: "Turning a 17-Type Data-Maintenance Backlog into a Calm, Prioritized Console",
    subtitle: "Supplier Data Console",
    description: "Redesigned the enterprise console procurement teams use to raise and track 17 types of SAP supplier master-data requests, replacing an undifferentiated wall of cards with a priority-first command center. Fully interactive demo below.",
    featured: true,
    color: "#7FD1FF",
    highlightBadge: "Live Interactive Demo",
    tags: [
      "Enterprise UX",
      "Design Systems",
      "React",
      "AG Grid",
      "Live Prototype"
    ],
    hasDemo: true
  },
  {
    id: "supplier-claims-tracker",
    slug: "supplier-claims-tracker",
    layout: "editorial",
    title: "Turning Scattered Supplier Recovery Claims into One Auditable Ledger",
    subtitle: "Supplier Claims Tracker",
    description: "Redesigned the console procurement teams use to raise supplier recovery claims, allocate reserves across 14 business units, and track each claim from intake to settlement.",
    color: "#FFAACB",
    highlightBadge: "Financial Recovery",
    tags: [
      "Enterprise UX",
      "Data Tables",
      "Financial Workflows",
      "Ant Design"
    ],
    hasDemo: true
  },
  {
    id: "neram-classes",
    slug: "neram-classes",
    title: "Designing Accessible Architecture Education for India's Underserved Students",
    subtitle: "India's 1st Architecture Exam Self Learning Platform",
    description: "Designed, built, and shipped neramclasses.com, a full-scale EdTech platform serving 1,000+ students across India, UAE, and Saudi Arabia.",
    featured: true,
    color: "#6FEAC8",
    highlightBadge: "AIR-1 Achievement",
    tags: [
      "Founder",
      "Product Design",
      "EdTech",
      "Full Stack"
    ],
    hasDemo: false
  },
  {
    id: "neram-application-journey",
    slug: "neram-application-journey",
    title: "Replacing a Generic Google Form With an Application That Speaks the Student's Language",
    subtitle: "Neram Classes: Student Application Journey",
    description: "Redesigned the NATA/JEE Paper 2 enrollment flow at neramclasses.com from a generic, duplicated Google Form into a multi-step application with its own content model and integrated payment.",
    color: "#6FEAC8",
    highlightBadge: "Shipped Redesign",
    tags: [
      "UX Writing",
      "Content Design",
      "Product Design",
      "EdTech"
    ],
    hasDemo: false
  },
  {
    id: "neram-nexus",
    slug: "neram-nexus",
    title: "Designing Status and Action Language for a Business Run by One Person",
    subtitle: "Neram Classes: Admin Operations (Neram Nexus)",
    description: "Applying content-design principles to Neram Nexus, the internal admin panel that runs enrollment, student progress, instructor scheduling, and content management for a 1,000+ student EdTech operation.",
    color: "#6FEAC8",
    highlightBadge: "Content Exploration",
    tags: [
      "UX Writing",
      "Content Design",
      "Admin UX",
      "EdTech"
    ],
    hasDemo: false
  },
  {
    id: "aesta",
    slug: "aesta",
    title: "Construction Management Made Simple for Small Builders",
    subtitle: "AESTA: Construction Management App",
    description: "Designed and built a construction management app for small builders in Tamil Nadu, tracking labor attendance, salary calculations, and material management in a mobile-first interface.",
    featured: true,
    color: "#FFDC72",
    highlightBadge: "Real-World Impact",
    tags: [
      "Construction Tech",
      "Product Design",
      "Mobile",
      "Full Stack"
    ],
    hasDemo: false
  },
  {
    id: "shared-expense-allocation",
    slug: "shared-expense-allocation",
    layout: "editorial-expense",
    title: "Two Sites, One Shared Bill, a Split That Changes Every Day",
    subtitle: "AESTA: Shared Expense Allocation",
    description: "Two co-located construction sites share one workforce and one tea-and-snacks vendor, so the daily cost split changes every day. A deep dive into moving that repetitive calculation off the site engineer and into the system that already held the attendance.",
    color: "#FFDC72",
    highlightBadge: "Workflow Deep Dive",
    tags: [
      "Workflow Design",
      "System Thinking",
      "Construction Tech",
      "Mobile"
    ],
    hasDemo: false
  }
];

// Same matching rules as getCaseStudyBySlug in caseStudies.js: canonical slug,
// id, or a legacy alias.
export const getCaseStudySummary = (slug) =>
  caseStudyIndex.find(
    (study) => study.slug === slug || study.id === slug || study.aliasSlugs?.includes(slug)
  );

export const getCaseStudyLayout = (slug) => getCaseStudySummary(slug)?.layout;

export const getFeaturedCaseStudies = () => caseStudyIndex.filter((study) => study.featured);

export default caseStudyIndex;
